// src/services/dev-asset-proxy.ts
//
// DEVELOPMENT ONLY. Compiled out of production builds by the NODE_ENV guard in install().
//
// THE PROBLEM
// Point clouds and splats are fetched straight from S3 and CloudFront using absolute URLs the
// API hands back. Both restrict by browser origin:
//
//   luxmodus-production-ftp CORS AllowedOrigins:
//     https://app.luxmodus3d.com, https://luxmodus3d.com,
//     https://share.luxmodus3d.com, https://www.luxmodus3d.com
//
// The dev origin (http://local.luxmodus3d.com:8080) is not among them, so the browser discards
// responses S3 has already served -- the .ply came back 200, then 206 on the range request, and
// was thrown away for a missing Access-Control-Allow-Origin. The CDN is stricter still and
// answers 403 outright, which Potree then tries to JSON.parse, producing "unexpected end of
// data at line 1 column 1".
//
// WHY NOT JUST ADD THE ORIGIN
// Because that is a change to production configuration to suit one developer's machine, and it
// would have to be repeated for every origin anyone ever develops from. The dev server can
// forward these itself, which means the browser only ever talks to localhost, same-origin, and
// CORS never enters into it. Nothing in AWS changes.
//
// WHY A NETWORK SHIM RATHER THAN REWRITING CALL SITES
// Three different consumers fetch these URLs -- Potree via XMLHttpRequest, the splat viewer via
// fetch, and the image-at-point code via fetch -- and the URLs are built in several places from
// API responses. Rewriting each call site would mean finding all of them and keeping them found.
// Intercepting the two transports catches every one, including inside third-party libraries.
//
// THE SIGNATURE STILL HOLDS. A presigned S3 URL signs the Host header, and the proxy is
// configured with changeOrigin so the upstream sees its own hostname. The query string is passed
// through untouched. This grants no access: without a valid signature from an authenticated API
// call, the proxy returns exactly the 403 S3 would have.

/** Absolute prefixes to route through the dev server, and the local path that forwards them. */
const ROUTES: { match: RegExp; path: string }[] = [
  // Signed S3 objects: splats, transforms.json, artifacts.
  { match: /^https:\/\/[a-z0-9.-]+\.s3\.[a-z0-9-]+\.amazonaws\.com/i, path: '/dev-s3' },
  // The published-point-cloud CDN that Potree reads cloud.js and octree nodes from.
  { match: /^https:\/\/[a-z0-9]+\.cloudfront\.net/i, path: '/dev-cdn' },
];

/**
 * Rewrite an absolute asset URL to the dev server, preserving path and query (the signature
 * lives in the query, so it must survive verbatim). Returns the input unchanged when it is not
 * one of the proxied hosts.
 */
export function toDevProxyUrl(input: string): string {
  for (const route of ROUTES) {
    const m = input.match(route.match);
    if (!m) continue;
    const rest = input.slice(m[0].length); // path + query, already encoded
    // The upstream host travels as a query parameter so one proxy path can serve any bucket or
    // distribution; the dev server reads it back and targets that host.
    const sep = rest.includes('?') ? '&' : '?';
    return `${route.path}${rest}${sep}__devHost=${encodeURIComponent(m[0])}`;
  }
  return input;
}

let installed = false;

/**
 * Patch fetch and XMLHttpRequest.open so every consumer is covered without knowing about them.
 * Idempotent, and a no-op outside development.
 */
export function installDevAssetProxy(): void {
  if (installed) return;
  if (process.env.NODE_ENV !== 'development') return;
  installed = true;

  const nativeFetch = window.fetch.bind(window);
  window.fetch = ((input: RequestInfo | URL, init?: RequestInit) => {
    if (typeof input === 'string') return nativeFetch(toDevProxyUrl(input), init);
    if (input instanceof URL) return nativeFetch(toDevProxyUrl(input.toString()), init);
    if (input instanceof Request && typeof input.url === 'string') {
      const rewritten = toDevProxyUrl(input.url);
      // Only rebuild the Request when the URL actually changed; cloning otherwise would
      // needlessly discard a body the caller may have set.
      if (rewritten !== input.url) return nativeFetch(new Request(rewritten, input), init);
    }
    return nativeFetch(input as RequestInfo, init);
  }) as typeof window.fetch;

  const nativeOpen = XMLHttpRequest.prototype.open;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  XMLHttpRequest.prototype.open = function (this: XMLHttpRequest, method: string, url: string | URL, ...rest: any[]) {
    const rewritten = typeof url === 'string' ? toDevProxyUrl(url) : toDevProxyUrl(url.toString());
    // eslint-disable-next-line prefer-spread
    return nativeOpen.apply(this, [method, rewritten, ...rest] as any);
  } as typeof XMLHttpRequest.prototype.open;

  // eslint-disable-next-line no-console
  console.info('[dev] S3 and CDN asset requests are routed through the dev server (CORS bypass, dev only).');
}
